import * as Yup from 'yup';

import { CheckboxField, NumberField, SearchSelectField } from '../..';
import {
  CheckboxTypeEnum,
  StatusDiscenteEnum,
  TipoFiltroBuscaEleitoresDiscenteEnum,
  TipoGrupoEleitoresEnum,
  TipoUnidadeAcademicaEnum,
} from '../../../enums';
import { FormikHelpers, FormikValues, getIn } from 'formik';
import { useCursos, useUnidadesAcademicas } from '../../../hooks';

import { ApiHelper } from '../../../helpers';
import { Axios } from 'axios';
import { DiscentesAtivosCursoDepartamentoCentroFormType } from '../../../types';
import { FunctionComponent } from 'react';
import { StatusCodes } from 'http-status-codes';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const initialValuesDiscentesAtivosCursoDepartamentoCentroForm = (
  t: Function,
  tipoFiltro: TipoFiltroBuscaEleitoresDiscenteEnum,
) => ({
  ano: '',
  ativo: true,
  ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO && {
    curso: '',
  }),
  ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO && {
    departamento: '',
  }),
  ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CENTRO && {
    centro: '',
  }),
  formando: true,
  matriculado: tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO,
  periodo: '',
  selectedForm: t(
    tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO
      ? 'label.discentes_ativos_por_curso'
      : tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO
        ? 'label.discentes_ativos_cursando_componentes_departamento'
        : 'label.discentes_ativos_por_centro',
  ),
  trancado: false,
});

export const validationSchemaDiscentesAtivosCursoDepartamentoCentroForm = (
  t: Function,
  tipoFiltro: TipoFiltroBuscaEleitoresDiscenteEnum,
) =>
  Yup.object()
    .shape({
      ano: Yup.number().when('matriculado', {
        is: true,
        then: (schema) => schema.required(t('validacao.campo_obrigatorio')),
      }),
      ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO && {
        curso: Yup.string().required(t('validacao.campo_obrigatorio')),
      }),
      ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO && {
        departamento: Yup.string().required(t('validacao.campo_obrigatorio')),
      }),
      ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CENTRO && {
        centro: Yup.string().required(t('validacao.campo_obrigatorio')),
      }),
      periodo: Yup.number().when('matriculado', {
        is: true,
        then: (schema) => schema.required(t('validacao.campo_obrigatorio')),
      }),
      selectedForm: Yup.string().required(t('validacao.campo_obrigatorio')),
    })
    .test({
      name: 'statusDiscentes',
      test: function (values: any) {
        const isValid = ['ativo', 'formando', 'trancado'].some((field) => values[field]);
        if (isValid) {
          return true;
        }
        return this.createError({
          message: t('validacao.campo_obrigatorio'),
          path: 'statusDiscentes',
        });
      },
    });

export const onSubmitDiscentesAtivosCursoDepartamentoCentroForm =
  (
    t: Function,
    axios: Axios,
    setResultado: Function,
    setLoading: Function,
    setErrorStatus: Function,
    errorHandler: Function,
    tipoFiltro: TipoFiltroBuscaEleitoresDiscenteEnum,
  ) =>
  (values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => {
    let statusDiscentes: number[] = [];
    values.ativo && statusDiscentes.push(StatusDiscenteEnum.ATIVO);
    values.formando && statusDiscentes.push(StatusDiscenteEnum.FORMANDO);
    values.trancado && statusDiscentes.push(StatusDiscenteEnum.TRANCADO);
    setLoading(true);
    setTimeout(() => {
      axios
        .get(ApiHelper.getUrl.apiSCE.buscaEleitores(TipoGrupoEleitoresEnum.DISCENTE), {
          params: {
            consulta:
              tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO
                ? 'ATIVO_CURSO'
                : tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO
                  ? 'ATIVO_DEPARTAMENTO'
                  : 'ATIVO_CENTRO',
            ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO && {
              curso: values.curso,
            }),
            ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO && {
              unidade: values.departamento,
            }),
            ...(tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CENTRO && {
              unidade: values.centro,
            }),
            matriculado: values.matriculado,
            status: statusDiscentes.join(','),
            ...(values.matriculado && { ano: values.ano, periodo: values.periodo }),
          },
        })
        .then((response) => {
          setResultado(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (error.response.status === StatusCodes.NOT_FOUND) {
            setErrorStatus(error.response.status);
          } else {
            errorHandler(error, t('erro.recuperar_eleitores'));
          }
        })
        .finally(() => {
          formikHelpers.setSubmitting(false);
        });
    }, 400);
  };

const DiscentesAtivosCursoDepartamentoCentroForm: FunctionComponent<DiscentesAtivosCursoDepartamentoCentroFormType> = ({
  formikProps,
  tipoFiltro,
}) => {
  const { t } = useTranslation();
  const hasTouchedStatusDiscentes =
    formikProps.touched?.['ativo'] || formikProps.touched?.['formando'] || formikProps.touched?.['trancado'];
  const cursos = useCursos(null, null);
  const unidades = useUnidadesAcademicas(
    tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO
      ? TipoUnidadeAcademicaEnum.DEPARTAMENTO
      : TipoUnidadeAcademicaEnum.CENTRO,
  );

  return (
    <div>
      {tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.CURSO ? (
        <SearchSelectField
          label={t('label.curso')}
          name="curso"
          formikProps={formikProps}
          options={cursos}
          required
          key="select-curso"
        />
      ) : tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO ? (
        <SearchSelectField
          label={t('label.departamento')}
          name="departamento"
          formikProps={formikProps}
          options={unidades}
          required
          key="select-departamento"
        />
      ) : (
        <SearchSelectField
          label={t('label.centro')}
          name="centro"
          formikProps={formikProps}
          options={unidades}
          required
          key="select-centro"
        />
      )}

      <legend
        className={classNames(
          'form-label',
          'mt-3',
          (formikProps.touched?.['ano'] || formikProps.touched?.['periodo']) &&
            (formikProps.errors?.['ano'] || formikProps.errors?.['periodo']) &&
            'text-danger',
        )}
      >
        {t('label.discentes_matriculados_em_turmas')}
      </legend>
      <fieldset
        className={classNames(
          'p-2 border rounded',
          (formikProps.touched?.['ano'] || formikProps.touched?.['periodo']) &&
            (formikProps.errors?.['ano'] || formikProps.errors?.['periodo']) &&
            'border-danger',
        )}
      >
        <CheckboxField
          formikProps={formikProps}
          label={t('label.sim_nao.true')}
          name="matriculado"
          type={CheckboxTypeEnum.SWITCH}
          {...{
            disabled: tipoFiltro === TipoFiltroBuscaEleitoresDiscenteEnum.DEPARTAMENTO,
          }}
        />
        {getIn(formikProps.values, 'matriculado') && (
          <div className="row pt-1">
            <div className="col">
              <NumberField formikProps={formikProps} label={t('label.ano')} name="ano" required maxLength={4} />
            </div>
            <div className="col">
              <NumberField formikProps={formikProps} label={t('label.periodo')} name="periodo" required maxLength={1} />
            </div>
          </div>
        )}
      </fieldset>

      <legend
        className={classNames(
          'form-label',
          'mt-3',
          hasTouchedStatusDiscentes && formikProps.errors?.['statusDiscentes'] && 'text-danger',
        )}
      >
        {t('label.status_discentes')}
      </legend>
      <fieldset
        className={classNames(
          'p-2 border rounded',
          hasTouchedStatusDiscentes && formikProps.errors?.['statusDiscentes'] && 'border-danger',
        )}
      >
        <div
          className={classNames(
            'd-flex gap-3',
            hasTouchedStatusDiscentes && formikProps.errors?.['statusDiscentes'] && 'is-invalid',
          )}
        >
          <CheckboxField formikProps={formikProps} label={t('label.ativo')} name="ativo" type={CheckboxTypeEnum.SWITCH} />
          <CheckboxField formikProps={formikProps} label={t('label.formando')} name="formando" type={CheckboxTypeEnum.SWITCH} />
          <CheckboxField formikProps={formikProps} label={t('label.trancado')} name="trancado" type={CheckboxTypeEnum.SWITCH} />
        </div>
        {hasTouchedStatusDiscentes && formikProps.errors?.['statusDiscentes'] && (
          <div className="invalid-feedback">{t('validacao.necessario_selecionar_status_busca_discente')}</div>
        )}
      </fieldset>
    </div>
  );
};

export default DiscentesAtivosCursoDepartamentoCentroForm;
